import { Weekday } from "@/data/model/Enums";
import type { IScheduleShift } from "@/data/model/OfferSchedule";
import { DateTime } from "@/data/util/DateTime";

import { i18n } from "@/portal/Trans";

export class PortalFormat {
    static toCommaSeparatedList<T>(
        entries: Array<T>,
        nameProperty: { (m: T): string },
        linkGenerator?: { (m: T): string },
        limit?: number
    ): JQuery | string {
        if (entries === undefined || entries.length === 0) return i18n !== undefined ? i18n("general.none") : "-";

        let str = "";
        for (const i in entries) {
            const prop = nameProperty(entries[i]);

            if (linkGenerator === undefined) str += prop + ", ";
            else {
                str += '<a href="' + linkGenerator(entries[i]) + '">' + prop + "</a>" + ", ";
            }

            if (limit !== undefined && limit <= parseInt(i) + 1) {
                const additional = entries.length - limit;
                if (additional > 0) {
                    if (str.length > 2) str = str.substring(0, str.length - 2);

                    let additionals = "";
                    for (let si = limit; si < entries.length; si++) additionals += nameProperty(entries[si]) + ", ";

                    if (additionals.length > 2) additionals = additionals.substring(0, additionals.length - 2);

                    str +=
                        i18n !== undefined
                            ? " " +
                              '<span data-toggle="tooltip" class="tooltiptrigger" title="' +
                              additionals +
                              '">' +
                              i18n("general.andMore", { qty: additional }) +
                              "</span>, "
                            : "";
                }

                break;
            }
        }

        if (str.length > 2) str = str.substring(0, str.length - 2);

        return str;
    }

    static timeRange(from?: Date, end?: Date): string {
        if (from === undefined && end === undefined) return i18n("general.timeFormat.always");

        if (from !== undefined && end === undefined)
            return i18n("general.timeFormat.fromStart", {
                start: DateTime.toDateString(from, true),
            });

        if (end !== undefined && from === undefined)
            return i18n("general.timeFormat.untilEnd", { end: DateTime.toDateString(end, true) });

        return i18n("general.timeFormat.range", {
            start: DateTime.toDateString(from!, true),
            end: DateTime.toDateString(end!, true),
        });
    }

    static timeSpanRange(from?: string, end?: string): string {
        if (from === undefined && end === undefined) return i18n("general.timeFormat.always");

        if (from !== undefined && end === undefined)
            return i18n("general.timeFormat.fromStart", { start: DateTime.timeSpanTotime(from) });

        if (end !== undefined && from === undefined)
            return i18n("general.timeFormat.untilEnd", { end: DateTime.timeSpanTotime(end) });

        return i18n("general.timeFormat.range", {
            start: DateTime.timeSpanTotime(from!),
            end: DateTime.timeSpanTotime(end!),
        });
    }

    static shiftTimes(shifts: Array<IScheduleShift>): string {
        let output = "";

        //create group by ranges
        const grouped = new Array<{ weekdayRange: Array<Weekday>; start: number; end: number }>();
        PortalFormat.eachShiftWeekday(shifts, (shift, wd, startT, endT) => {
            const existing = grouped.find((c) => c.start == startT && c.end === endT);
            if (existing !== undefined) {
                //extend weekday
                if (existing.weekdayRange.find((c) => c === wd) !== undefined) return;

                existing.weekdayRange.push(wd);
            } else {
                grouped.push({ weekdayRange: [wd], start: startT, end: endT });
            }
        });

        //daily
        let daily = grouped.filter((c) => c.weekdayRange.length === 7);
        if (daily.length > 0) {
            daily = daily.sort((a, b) => a.start - b.start);
            let dailyStr = i18n("general.timeFormat.daily") + ": ";
            for (const di in daily) {
                const timeRange = daily[di];
                dailyStr += PortalFormat.shiftTimeRangeToString(timeRange.start, timeRange.end) + ", ";
                const idx = grouped.indexOf(timeRange);
                if (idx > -1) grouped.splice(idx, 1);
            }

            output += dailyStr.substring(0, dailyStr.length - 2) + "<br />";
        }

        if (grouped.length !== 0) {
            let outputables = new Array<{
                fromW: Weekday;
                toW?: Weekday;
                slots: Array<{ start: number; end: number }>;
            }>();

            do {
                const nextGroup = grouped[0];
                if (nextGroup === undefined) break;

                const sortedWeekdays = nextGroup.weekdayRange.sort((a, b) => a - b);

                //get a range of weekdays that are next to each other
                let weekStart = sortedWeekdays[0];
                let weekRange = new Array<Weekday>();
                for (const wi in sortedWeekdays) {
                    const checkWeek = sortedWeekdays[wi];
                    if (!DateTime.weekdayIsNextTo(checkWeek, weekStart)) break;

                    weekRange.push(checkWeek);
                    weekStart = checkWeek;
                }

                if (weekRange.length === 0) weekRange = [...sortedWeekdays];

                //search for other groups with the same weekday range
                let weekGroups = grouped.filter(
                    (c) =>
                        c.weekdayRange.length === weekRange.length &&
                        c.weekdayRange.find((d) => !weekRange.includes(d)) === undefined
                );
                if (weekGroups.length === 0) {
                    //no exact group for that range, change root tree by splitting up range
                    const newGroup = { start: nextGroup.start, end: nextGroup.end, weekdayRange: weekRange };
                    weekGroups = [newGroup];
                    grouped.push(newGroup);
                    nextGroup.weekdayRange = nextGroup.weekdayRange.filter((c) => !weekRange.includes(c));
                }

                if (weekGroups.length > 0) {
                    const firstWsGroup = weekGroups[0];
                    const outputG = { fromW: undefined, toW: undefined, slots: [] } as any;

                    if (firstWsGroup.weekdayRange.length === 1) {
                        outputG.fromW = firstWsGroup.weekdayRange[0];
                    } else {
                        const sorted = firstWsGroup.weekdayRange.sort((a, b) => a - b);
                        outputG.fromW = sorted[0];
                        outputG.toW = sorted[sorted.length - 1];
                    }

                    //check for existing output group
                    for (const wkgi in weekGroups) {
                        const weekGroup = weekGroups[wkgi];
                        outputG.slots.push({ start: weekGroup.start, end: weekGroup.end });

                        const idx = grouped.indexOf(weekGroup);
                        if (idx > -1) grouped.splice(idx, 1);
                    }

                    //check for existing group and merge
                    const existing = outputables.find((c) => c.fromW === outputG.fromW && c.toW === outputG.toW);
                    if (existing !== undefined) {
                        for (const sli in outputG.slots) existing.slots.push(outputG.slots[sli]);
                    } else {
                        outputables.push(outputG);
                    }
                }
            } while (grouped.length > 0);

            //outout groups
            outputables = outputables.sort((a, b) => a.fromW - b.fromW);
            for (const i in outputables) {
                let ws = "";
                const outputGroup = outputables[i];

                if (outputGroup.toW === undefined) ws = i18n("enums.Weekday." + Weekday[outputGroup.fromW]) + ": ";
                else
                    ws +=
                        i18n("enums.Weekday." + Weekday[outputGroup.fromW]) +
                        " - " +
                        i18n("enums.Weekday." + Weekday[outputGroup.toW]) +
                        ": ";

                const orderedSlots = outputGroup.slots.sort((a, b) => a.start - b.start);
                for (const sli in orderedSlots)
                    ws += PortalFormat.shiftTimeRangeToString(orderedSlots[sli].start, orderedSlots[sli].end) + ", ";

                output += ws.substring(0, ws.length - 2) + "<br />";
            }
        }

        return output;
    }

    static shiftTimeRangeToString(start: number, end: number, writeUntil?: boolean): string {
        const untils = writeUntil !== undefined && writeUntil === true ? i18n("general.timeFormat.until") : "-";
        return start === 0 && end === 60 * 60 * 24 - 1
            ? i18n("general.timeFormat.wholeDay")
            : DateTime.secondsToTime(start) + " " + untils + " " + DateTime.secondsToTime(end);
    }

    static shiftDailyRangeToString(start: Date, end: Date): string {
        // the planning times are Swiss wall-clock times read as UTC (DateTime.parseIsoDate), so today has to be the Swiss
        // date too: the UTC one is still yesterday in the first hour or two after midnight, and every slot of today then
        // looked as if it ran into tomorrow, which made one starting at 00:00 a "full day"
        const swissToday = new Intl.DateTimeFormat("en-US", {
            timeZone: "Europe/Zurich",
            year: "numeric",
            month: "numeric",
            day: "numeric",
        }).formatToParts(new Date());
        const swissDatePart = (type: Intl.DateTimeFormatPartTypes) =>
            Number(swissToday.find((part) => part.type === type)!.value);

        const nowS = new Date(swissDatePart("year"), swissDatePart("month") - 1, swissDatePart("day"));
        const sS = new Date(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
        const eS = new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

        let startTime = start.getUTCHours() * 60 * 60 + start.getUTCMinutes() * 60 + start.getUTCSeconds();
        let endTime = end.getUTCHours() * 60 * 60 + end.getUTCMinutes() * 60 + end.getUTCSeconds();

        //round to daystart
        if (sS < nowS) startTime = 0;

        if (eS > nowS) endTime = 60 * 60 * 24 - 1;

        return PortalFormat.shiftTimeRangeToString(startTime, endTime, true);
    }

    static eachShiftWeekday(
        shifts: Array<IScheduleShift>,
        handler: { (shift: IScheduleShift, weekday: Weekday, startTime: number, endTime: number) }
    ) {
        for (const i in shifts) {
            const shift = shifts[i];
            let forWeekdays = shift.weekdays;
            const startSeconds = shift.wholeDay
                ? 0
                : shift.from === undefined
                  ? 0
                  : DateTime.secondsOfTimeSpan(shift.from);
            const endSeconds = shift.wholeDay
                ? 60 * 60 * 24 - 1
                : shift.until === undefined
                  ? 60 * 60 * 24 - 1
                  : DateTime.secondsOfTimeSpan(shift.until);

            if (forWeekdays.length === 0) {
                forWeekdays = [
                    Weekday.Monday,
                    Weekday.Tuesday,
                    Weekday.Wednesday,
                    Weekday.Thursday,
                    Weekday.Friday,
                    Weekday.Saturday,
                    Weekday.Sunday,
                ];
            }

            for (const wi in forWeekdays) handler(shift, forWeekdays[wi], startSeconds, endSeconds);
        }
    }
}
